import React, {
  Fragment,
  useState,
  useEffect,
  useCallback,
  useRef,
} from "react";
import ActionBar from "./ActionBar";
import _ from "lodash";
import { navigate, useLocation } from "@reach/router";
import styled from "styled-components";
import themes from "../../../assets/theme";
import COLORS from "../../../assets/Colors";
import useLoader from "../../../hooks/useLoader";
import CaseService from "../../../services/CaseService";
import theme from "../../../assets/theme";
import TranscriptEditor from "../../transcripts/TranscriptEditor/index";
import { Formik } from "formik";
import {
  Grid,
  RadioGroup,
  FormControlLabel,
  Radio,
  makeStyles,
} from "@material-ui/core";
import TranscriptionService from "../../../services/TranscriptionService";

const useStyles = makeStyles((themes) => ({
  formLabel: {
    fontFamily: theme.fonts.primaryFontSemiBold,
    color: COLORS.COLOR_DARK,
  },
  radioRoot: {
    margin: 15,
    [themes.breakpoints.up(640)]: {
      margin: "0px 26px",
    },
    "& .MuiTab-root": {
      padding: "5px 0",
      [themes.breakpoints.up(640)]: {
        padding: "6px 12px",
      },
    },
    "& .MuiTab-wrapper": {
      fontSize: 12,
      [themes.breakpoints.up(640)]: {
        fontSize: 16,
      },
    },
  },
}));

const TranscriptEdit = (id) => {
  const history = useLocation();
  const resolutionKind = history.search.replace("?caseType=", "");
  const breadcrumbs = [
    "Cases",
    `${_.capitalize(resolutionKind)} - ${id?.id}`,
    "Transcript",
  ];
  // const { enqueueSnackbar } = useSnackbar();
  const { setLoader } = useLoader();
  const caseId = id?.id;
  const [data, setData] = useState([
    { id: "", start: "", end: "", text: "", speaker: "", confidence: "" },
  ]);
  const [mediaUrl, setMediaUrl] = useState();
  const [meetingId, setMeetingId] = useState();
  const [refreshDetails, setRefreshDetails] = useState(true);
  const [render, setRender] = useState(false);
  const [checked, setChecked] = useState(false);
  let LiveTranscription = true;
  const [trigger, setTrigger] = useState(false);
  const [streamingKey, setStreaminKey] = useState();
  const [meetingType, setMeetingType] = useState("plain");
  const classes = useStyles();

  const meetingTypeRef = useRef(meetingType);

  useEffect(() => {
    meetingTypeRef.current = meetingType;
  }, [meetingType]);

  const onBreadcrumbClick = (selected) => {
    switch (_.snakeCase(selected)) {
      case `${resolutionKind}_${id?.id}`:
        navigate(`/view/cases/${id?.id}?caseType=${resolutionKind}`);
        break;
      case "cases":
        navigate(`/view/cases?caseType=${resolutionKind}`);
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    async function getLiveTranscriptionData(caseId) {
      try {
        setLoader({ state: true, message: "Fetching Transcription..." });
        const res = await CaseService.getLiveTranscriptionData(caseId);
        if (res) {
          setData(
            res?.transcriptionData?.words?.map((el) => {
              return {
                id: el?.id,
                end: el?.end,
                confidence: el?.confidence,
                text: el.text ? el?.text : "",
                start: el?.start,
                speaker: el?.speaker,
              };
            }),
          );
          setMeetingId(res?.meetingId);
          setMediaUrl(res);
        } else {
          setMediaUrl();
        }
      } catch (error) {
        console.log(error);
      } finally {
        setLoader({ state: false });
        setRender(true);
      }
    }
    if (refreshDetails) {
      getLiveTranscriptionData(caseId);
      setRefreshDetails(false);
    }
  }, [refreshDetails]); // eslint-disable-line react-hooks/exhaustive-deps

  // useEffect(() => {
  //   const interval = setInterval(() => {
  //     (async function () {
  //       const response = await CaseService.getTranscriptionRefreshStatus(
  //         meetingId
  //       );
  //       if (response?.neutralRefreshStatus) {
  //         setRefreshDetails(true);
  //       }
  //     })();
  //   }, 10 * 1000);
  //   return () => clearInterval(interval);
  // }, [meetingId]);

  useEffect(() => {
    if (checked) {
      const timeInterval = setInterval(() => {
        async function getLiveTranscriptionData(caseId) {
          try {
            setTrigger(true);
            const res = await CaseService.getLiveTranscriptionData(caseId);
            if (res) {
              setData(
                res?.transcriptionData?.words?.map((el) => {
                  return {
                    id: el?.id,
                    end: el?.end,
                    confidence: el?.confidence,
                    text: el.text ? el?.text : "",
                    start: el?.start,
                    speaker: el?.speaker,
                  };
                }),
              );
              setMeetingId(res?.meetingId);
              setMediaUrl(res);
            } else {
              setMediaUrl();
            }
          } catch (error) {
            console.log(error);
          } finally {
            setRender(true);
            setTrigger(false);
          }
        }
        getLiveTranscriptionData(caseId);
      }, 3 * 1000);
      return () => clearInterval(timeInterval);
    }
  }, [checked]); // eslint-disable-line react-hooks/exhaustive-deps

  const mapWords = (words = []) =>
    words.map((el) => ({
      id: el?.id,
      end: el?.end,
      confidence: el?.confidence,
      text: el.text ? el?.text : "",
      start: el?.start,
      speaker: el?.speaker,
    }));

  const fetchTranscriptionData = useCallback(
    async ({ showLoader = true, isPolling = false } = {}) => {
      try {
        if (showLoader && !isPolling) {
          setLoader({ state: true, message: "Fetching Transcription..." });
        }
        if (isPolling) setTrigger(true);

        const res = await CaseService.getLiveTranscriptionData(caseId);
        const currentMeetingType = meetingTypeRef.current;

        if (currentMeetingType === "edited") {
          // Edited mode: populate only if data exists
          if (res?.transcriptionData?.words?.length > 0) {
            setData(mapWords(res.transcriptionData.words));
            setMeetingId(res?.meetingId ?? null);
            setMediaUrl(res);
          } else {
            setData([]);
            setMeetingId(null);
            setMediaUrl(null);
          }
        } else {
          // Plain (raw) mode
          if (res?.transcriptionData?.words?.length > 0) {
            setData(mapWords(res.transcriptionData.words));
            setMeetingId(res?.meetingId ?? null);
            setMediaUrl(res);
          } else {
            setData([]);
            setMeetingId(null);
            setMediaUrl(null);
          }
        }

        // Fetch streaming key only on initial load (not during polling)
        if (!isPolling && res?.meetingId) {
          const streamKey = await TranscriptionService.generateRTMPLink(
            res.meetingId,
          );
          if (streamKey?.streaming_key) {
            setStreaminKey(streamKey.streaming_key);
          }
        }
      } catch (error) {
        console.log(error);
      } finally {
        if (!isPolling) {
          setLoader({ state: false });
          setRender(true);
        } else {
          setTrigger(false);
          setRender(true);
        }
      }
    },
    [caseId, setLoader],
  );

  const actions = [
    {
      text: "Refresh",
      props: {
        onClick: () => fetchTranscriptionData({ showLoader: true }),
      },
      hidden: meetingType === "plain",
    },
  ];

  const radio_inputs = [
    { label: "Raw Transcript", name: "plain", value: "plain" },
    { label: "Edited Transcript", name: "edited", value: "edited" },
  ];

  return (
    <Fragment>
      <ActionBar
        {...{
          breadcrumbs,
          onBreadcrumbClick,
          actions,
          LiveTranscription,
          checked,
          setChecked,
          meetingType,
        }}
      />
      <Formik
        initialValues={{ meetingType: "plain" }}
        enableReinitialize
        validateOnBlur
        validateOnChange
        onSubmit={""}
      >
        {({ values, setFieldValue }) => (
          <form>
            <Grid
              className={classes.radioRoot}
              item
              xs={12}
              spacing={2}
              style={{ marginTop: "5px", padding: 16 }}
            >
              <RadioGroup
                style={{ flexDirection: "row", marginLeft: "10px" }}
                name={"meetingType"}
                value={values.meetingType}
                onChange={(e) => {
                  const { value } = e.target;
                  setMeetingType(value);
                  meetingTypeRef.current = value;
                  setFieldValue("meetingType", value);

                  if (value === "edited") {
                    // Clear stale data and re-fetch in edited mode
                    setData([]);
                    fetchTranscriptionData({ showLoader: true });
                  } else {
                    // Switch back to raw: stop polling, re-fetch raw data
                    setChecked(false);
                    setData([
                      {
                        id: "",
                        start: "",
                        end: "",
                        text: "",
                        speaker: "",
                        confidence: "",
                      },
                    ]);
                    fetchTranscriptionData({ showLoader: true });
                  }
                }}
              >
                {radio_inputs.map((radio, key) => (
                  <FormControlLabel
                    key={key}
                    classes={{ label: classes.formLabel }}
                    value={radio.value}
                    control={
                      <Radio size="medium" style={{ color: "#00838c" }} />
                    }
                    label={radio.label}
                  />
                ))}
              </RadioGroup>
            </Grid>
            <Container>
              <div>
                {values?.meetingType === "edited" && !data.length ? (
                  <StyledTableEmptyBlock>
                    <h2>
                      {
                        "Welcome to JustAct live transcription services. Your session has either not started or your live transcript is currently being generated."
                      }
                    </h2>
                  </StyledTableEmptyBlock>
                ) : (
                  <TranscriptEditor
                    {...{
                      caseId,
                      meetingId,
                      render,
                      setRender,
                      trigger,
                      streamingKey,
                      setData,
                    }}
                    data={data || []}
                    mediaUrl={mediaUrl?.audioFileUrl}
                    title={history?.state?.title}
                    meetingType={values?.meetingType}
                  />
                )}
              </div>
            </Container>
          </form>
        )}
      </Formik>
    </Fragment>
  );
};

export default TranscriptEdit;

export const Container = styled.div`
  padding: 4px 43px;
  display: flex;
  flex-direction: column;
`;

export const HeadingContainer = styled.span`
  margin-bottom: 31px;
  font-family: ${themes.fonts.primaryFontSemiBold};
  font-size: ${(props) => (props.size ? props.size : "13px")};
  color: ${COLORS.BTN_GREEN};
  // text-transform: uppercase;
  cursor: pointer;
  & .topSection {
    background: ${COLORS.BOX_DARKGREEN};
  }
`;

const StyledTableEmptyBlock = styled.div`
  position: absolute;
  top: 55%;
  left: 55%;
  transform: translate(-40%, -40%);
  display: flex;
  flex-direction: column;
  align-items: center;
  h2 {
    color: ${COLORS.COLOR_DARK};
    font-weight: 500;
    letter-spacing: 0.1px;
    font-size: 20px;
    margin: 0;
    text-align: center;
    font-family: ${theme.fonts.primaryFontBold};
  }
  p {
    margin: 10px auto 30px;
    font-family: ${theme.fonts.primaryFontRegular};
    color: ${COLORS.COLOR_DARK};
  }
  a {
    color: ${COLORS.BTN_GREEN};
    font-family: ${theme.fonts.primaryFontRegular};
  }
`;
